import React, { useRef, useEffect } from 'react'
import '../styles/pages/snippets.scss'
import { motion } from 'framer-motion'
import codeIcon from '../pages/posts/images/svgs/pawn9.png'
import Headline from '../components/Headline'
import { gsap, Back } from '../gsap/gsap-core'
import { CSSPlugin } from '../gsap/CSSPlugin'
import { Helmet } from 'react-helmet'

gsap.registerPlugin(CSSPlugin)

const SnippetsPage = () => {
  let pref = useRef(null)
  useEffect(() => {
    gsap.fromTo(
      pref.current,
      {
        duration: .5,
        css: {
          opacity: 0,
          scaleX: 0,
          rotationX: -180,
       
          transformStyle: '-50%, -50%',
          autoAlpha: 0,
        },
        ease: Back.easeOut,
      },
    
      {
        scaleX: 1.15,
        perspective: 400,
        ease: Back.easeInOut,
      }
    )

    return () => pref.current
  }, [])
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <Helmet
        defaultTitle="Snippets"
        title={'Snippets Page'}
        description={[
          'useWindowSize',
          'Web Development',
          'FrontEnd Development',
          'JavaScript',
        ]}
        lang={'en'}
      />
      <div className="snippets-wrapper">
        <Headline />

        <div className="snippets-wrapper__main-content">

          <section className="snippet-item">
            <h3>
              <span>
                <img
                  ref={(ele) => (pref.current = ele)}
                  src={codeIcon}
                  width={143}
                  height={192}
                  alt="codeIcon img"
                />
              </span>
              useWindowSize
            </h3>
            <p>
              To get the current size of the browser window. It returns an
              object containing the window's width and height. If executed
              server-side, the value of width and height will be undefined.
            </p>

            <pre
              style={{
                backgroundColor: 'rgb(11 35 45)',
                display: 'flex',
                flexWrap: 'wrap',
                width: '100%',
                overflowY: 'scroll',
                justifyContent: 'center',
                letterSpacing: '.3px',
              }}
            >
              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: '#faf41f',
                  fontFamily: 'var(--fontcode)',

                  textAlign: 'left',
                }}
              >
                {`                
                   import { useState, useEffect } from 'react'
                    // Usage
                    const App = () => {
                      const size = useWindowSize();
                      return (
                        <div>
                          {DEVICE_SIZES.width}px / {size.height}px
                        </div>
                      );
                    }
                  `}
              </code>
              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: 'rgb(240 240 240)',
                  fontFamily: 'var(--fontcode)',

                  textAlign: 'left',
                }}
              >{`   
 // Hook
 const useWindowSize = () => {
  // Initialize state with undefined width/height so server and 
  // and client renders match 
    const [windowSize, setWindowSize] = useState({
      width: undefined,
      height: undefined,
    });

    useEffect(() => {
      // Handler to call on window resize
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call right away
    handleResize();
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize)
    
    },[]) //only runs on mount

    return windowSize;

  }
     `}</code>
            </pre>
          </section>

          <section className="snippet-item">
            <h3>
              <span>
                <img
                  ref={(ele) => (pref.current = ele)}
                  src={codeIcon}
                  width={143}
                  height={192}
                  alt="codeIcon img"
                />
              </span>
              React Sticky Hook - Ref
            </h3>
            <p>
              A React hook for observing position: sticky state on refs.
              useSticky returns a pair of values: the ref to observe and the
              current sticky state of the ref.
            </p>

            <pre
              style={{
                backgroundColor: 'rgb(11 35 45)',
                display: 'flex',
                flexWrap: 'wrap',
                width: '100%',
                overflowY: 'scroll',
                justifyContent: 'center',
                letterSpacing: '.3px',
              }}
            >
              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: '#faf41f',
                  fontFamily: 'var(--fontcode)',

                  textAlign: 'left',
                }}
              >
                {`                
  import { useEffect, useRef, useState } from "react";

  /**
    * Returns a ref, and a stateful value bound to the ref
    * @returns [Any, Boolean]
    * //robinjohnson repo
    */
`}
              </code>
              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: '#e4f3ff',
                  fontFamily: 'var(--fontcode)',
                  textAlign: 'left',
                }}
              >{`  

  export function useSticky() {
    const stickyRef = useRef(null);
    const [sticky, setSticky] = useState(false);
    const eventsToBind = [
      [document, "scroll"],
      [window, "resize"],
      [window, "orientationchange"]
    ]; 
  `}</code>

              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: '#ff8987',
                  fontFamily: 'var(--fontcode)',
                  textAlign: 'left',
                }}
              >{`   
  useEffect(() => {
    // Observe when ref enters or leaves sticky state
    // rAF https://stackoverflow.com/questions/41740082/scroll-events-requestanimationframe-vs-requestidlecallback-vs-passive-event-lis
    function observe() {
      const refPageOffset = stickyRef.current.getBoundingClientRect().top;
      const stickyOffset = parseInt(getComputedStyle(stickyRef.current).top);
      const stickyActive = refPageOffset <= stickyOffset;

      if (stickyActive && !sticky) setSticky(true);
      else if (!stickyActive && sticky) setSticky(false);
    }

    observe();

    // Bind events
    eventsToBind.forEach(eventPair => {
      eventPair[0].addEventListener(eventPair[1], observe);
    });
  
  `}</code>

              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: '#fafafa',
                  fontFamily: 'var(--fontcode)',
                  textAlign: 'left',
                }}
              >{`   
    return () => {
      eventsToBind.forEach(eventPair => {
        eventPair[0].removeEventListener(eventPair[1], observe);
      });
    };

    }, [stickyRef, sticky]);

    return [stickyRef, sticky];
  }
     `}</code>
            </pre>
          </section>

          <section className="snippet-item">
            <h3>
              <span>
                <img
                  ref={(ele) => (pref.current = ele)}
                  src={codeIcon}
                  width={143}
                  height={192}
                  alt="codeIcon img"
                />
              </span>
              React form component
            </h3>
            <p>A simple login form component</p>

            <pre
              style={{
                backgroundColor: 'rgb(11 35 45)',
                display: 'flex',
                flexWrap: 'wrap',
                width: '100%',
                overflowY: 'scroll',
                justifyContent: 'center',
                letterSpacing: '.3px',
              }}
            >
              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: '#faf41f',
                  fontFamily: 'var(--fontcode)',

                  textAlign: 'left',
                }}
              >
                {`                
                     import React, { useState } from "react";
                    
                     const defaultFormData = {
                       username: "",
                       password: ""
                     };
                     
                    `}
              </code>

              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: '#fff478',
                  fontFamily: 'var(--fontcode)',

                  textAlign: 'left',
                }}
              >
                {`   
                 // Form component
                 const Form = () => {
                  const [user, setUser] = useState(defaultFormData);
                
                  const { username, password } = user;

                `}
              </code>

              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: '#ff8987',
                  fontFamily: 'var(--fontcode)',
                  textAlign: 'left',
                }}
              >
                {`   
                  const handleChange = (e) => {
                    setUser((prevState) => ({
                      ...prevState,
                      [e.target.id]: e.target.value
                    }));
                  };
                
                  const handleSubmit = (e) => {
                    e.preventDefault();
                    console.log(user);
                    setUser(defaultFormData);
                  };

                 `}
              </code>

              <code
                style={{
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  margin: 0,
                  padding: 0,
                  color: 'rgb(240 240 240)',
                  fontFamily: 'var(--fontcode)',
                  textAlign: 'left',
                }}
              >
                {`
                      return (
                        <form onSubmit={handleSubmit}>
                          <h3>Signin</h3>
                    
                          <label htmlFor="username">Username</label>
                    
                          <input
                            onChange={(e) => handleChange(e)}
                            type="text"
                            id="username"
                            value={username}
                            placeholder="enter username"
                          />
                    
                          <label htmlFor="password">Password</label>
                    
                          <input
                            onChange={(e) => handleChange(e)}
                            type="password"
                            id="password"
                            value={password}
                            placeholder="enter password"
                          />
                    
                          <button type="submit">Submit</button>
                        </form>
                      );
                    }
                    
                  


                `}
              </code>
            </pre>
          </section>
        </div>
      </div>
    </motion.div>
  )
}

export default SnippetsPage
